/* ============================================================================
   Enshield public design system — SECTION PATTERNS (React)
   ----------------------------------------------------------------------------
   Recurring compositions, defined once. A page picks a pattern and supplies
   content; it does not re-lay-out the pattern.
   ========================================================================== */
import type { ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Band, Btn, Container, Eyebrow } from './primitives';
import { ScrollReveal } from '../components/ScrollReveal';
import { Icon, type IconName } from './icons';
import { IntelligenceField } from './IntelligenceField';
import { ART } from './media';
import { MotionArtwork, MotionAstronaut, MotionSection, MotionStagger, MotionStaggerItem, useNarrowMotionViewport } from '../components/motion';
import { publicMotion, publicTransition } from '../lib/motion';

/* ---- Hero ---------------------------------------------------------------
   `art` is optional. When a route has no hero artwork the copy centres
   rather than sitting in a half-empty two-column grid. */
export function Hero({
  eyebrow,
  title,
  titleId,
  lead,
  actions,
  below,
  art,
  compact,
  center,
  field,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  titleId?: string;
  lead?: ReactNode;
  actions?: ReactNode;
  below?: ReactNode;
  art?: ReactNode;
  compact?: boolean;
  center?: boolean;
  /** Swap the flat CSS wash for the living Intelligence Field. Reserved for
      the two visual peaks — the home hero and the closing CTA — so it stays
      a signature rather than a page-wide texture. */
  field?: boolean;
}) {
  const reducedMotion = useReducedMotion();
  const narrow = useNarrowMotionViewport();
  const centred = center ?? !art;
  const distance = narrow ? publicMotion.distance.micro : publicMotion.distance.interface;
  const slotVariants = {
    hidden: reducedMotion ? {} : { opacity: 0, y: distance },
    show: { opacity: 1, y: 0, transition: publicTransition(Boolean(reducedMotion), publicMotion.duration.scene) },
  };
  const copy = (
    <div className="p-hero__copy" data-motion="hero-copy">
      {eyebrow ? <Eyebrow pill>{eyebrow}</Eyebrow> : null}
      <motion.h1 data-motion="hero-headline" variants={slotVariants} id={titleId} className={compact ? 'u-h1' : 'u-display'}>{title}</motion.h1>
      {lead ? <motion.p data-motion="hero-support" variants={slotVariants} className="u-lead">{lead}</motion.p> : null}
      {actions ? <motion.div data-motion="hero-actions" variants={slotVariants} className="u-cluster p-hero__actions">{actions}</motion.div> : null}
      {below}
    </div>
  );

  return (
    <motion.section
      data-motion="hero-scene"
      data-motion-distance={distance}
      className={['p-hero', compact && 'p-hero--compact', centred && 'p-hero--center', field && 'p-hero--field'].filter(Boolean).join(' ')}
      aria-labelledby={titleId}
      initial={reducedMotion ? false : 'hidden'}
      animate="show"
      variants={{ hidden: {}, show: { transition: { staggerChildren: reducedMotion ? 0 : publicMotion.stagger.hero } } }}
    >
      {field ? <IntelligenceField /> : null}
      <Container>
        {art ? <div className="p-hero__grid">{copy}<div data-motion="hero-artwork" data-motion-slot="independent">{art}</div></div> : copy}
      </Container>
    </motion.section>
  );
}

/* ---- Astronaut artwork ---------------------------------------------------
   The astronaut is the signature Enshield device, and this is the only place
   it is allowed to move. `drift` gives the one dominant hero figure a very
   slow vertical float; every supporting appearance is static and simply
   fades in. Reduced motion removes the travel and keeps the final state.

   `priority` marks the LCP hero image: it loads eagerly and is decoded
   synchronously, while every other appearance is lazy. */
export function AstronautArt({
  src,
  width,
  height,
  drift,
  priority,
  support,
  className,
}: {
  src: string;
  width: number;
  height: number;
  drift?: boolean;
  priority?: boolean;
  support?: boolean;
  className?: string;
}) {
  return (
    <MotionArtwork support={support} className={['u-art', support ? 'u-art--support' : 'u-art--hero', className].filter(Boolean).join(' ')}>
      <div aria-hidden="true"><MotionAstronaut src={src} width={width} height={height} priority={priority} drift={drift} /></div>
    </MotionArtwork>
  );
}

/* ---- Platform / commerce trust strip ------------------------------------ */
export const SUPPORTED_PLATFORMS = [
  { name: 'Shopify', icon: 'store' as IconName },
  { name: 'Miva', icon: 'globe' as IconName },
];

export function PlatformStrip({ label = 'Connects to the commerce platforms you already sell on' }: { label?: string }) {
  return (
    <Band space="compact" tone="alt" label="Supported commerce platforms">
      <div className="p-strip">
        <p className="p-strip__label">{label}</p>
        {SUPPORTED_PLATFORMS.map((platform) => (
          <span key={platform.name} className="p-strip__item">
            <Icon name={platform.icon} />
            {platform.name}
          </span>
        ))}
      </div>
    </Band>
  );
}

/* ---- Value column ------------------------------------------------------- */
export function ValueColumn({ icon, title, children }: { icon: IconName; title: string; children: ReactNode }) {
  return (
    <div className="p-value">
      <span className="u-plate" aria-hidden="true"><Icon name={icon} /></span>
      <h3 className="u-h3">{title}</h3>
      <p>{children}</p>
    </div>
  );
}

/* ---- Checklist ---------------------------------------------------------- */
export function Checklist({ items, className }: { items: string[]; className?: string }) {
  const reducedMotion = useReducedMotion();
  return (
    <motion.ul
      data-motion="motion-list"
      className={['p-checks', className].filter(Boolean).join(' ')}
      initial={reducedMotion ? false : 'hidden'}
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      variants={{ hidden: {}, show: { transition: { staggerChildren: reducedMotion ? 0 : 0.06 } } }}
    >
      {items.map((item) => (
        <motion.li
          key={item}
          variants={{ hidden: reducedMotion ? {} : { opacity: 0, x: -10 }, show: { opacity: 1, x: 0 } }}
          transition={publicTransition(!!reducedMotion)}
        ><Icon name="check" />{item}</motion.li>
      ))}
    </motion.ul>
  );
}

/* ---- Customer essentials -----------------------------------------------
   A compact, reusable orientation band for shoppers who arrive on a
   merchant-facing page. It explains the customer path without adding new
   coverage promises or competing with the primary page action. */
const customerEssentials = [
  {
    icon: 'shield-check' as const,
    title: 'Confirm your order was protected',
    body: 'Look for Enshield Package Protection in your checkout or order confirmation before starting a claim.',
  },
  {
    icon: 'clipboard' as const,
    title: 'Keep order details nearby',
    body: 'Your order number and the email used at checkout help connect your request to the right store.',
  },
  {
    icon: 'lock' as const,
    title: 'Follow updates privately',
    body: 'After filing, use the private link or tracking token in your Enshield email to check progress.',
  },
];

export function CustomerEssentials() {
  return (
    <div data-testid="customer-essentials">
      <MotionSection className="p-customer-essentials" aria-label="For customers">
        <Container>
          <div className="p-customer-essentials__intro">
            <Eyebrow>For customers</Eyebrow>
            <h2 className="u-h2">For customers: help with your order</h2>
            <p className="u-body">For a protected order, start with the information you received from the store. You can file or track a claim without creating an Enshield account.</p>
            <div className="u-cluster">
              <Btn to="/file-a-claim">File a claim</Btn>
              <Btn tone="secondary" to="/track-claim">Track a claim</Btn>
            </div>
          </div>
          <MotionStagger className="p-customer-essentials__steps">
            {customerEssentials.map((item) => (
              <MotionStaggerItem key={item.title}>
                <article className="p-customer-essential">
                  <span className="u-plate" aria-hidden="true"><Icon name={item.icon} /></span>
                  <div><h3 className="u-h4">{item.title}</h3><p className="u-small">{item.body}</p></div>
                </article>
              </MotionStaggerItem>
            ))}
          </MotionStagger>
        </Container>
      </MotionSection>
    </div>
  );
}

/* ---- Closing CTA --------------------------------------------------------
   Shared closing action. Signature variants reserve a separate artwork
   column for the approved guide, never behind text or actions. */
export function FinalCTA({
  title,
  body,
  primary,
  secondary,
  titleId,
  signature,
}: {
  title: ReactNode;
  body?: string;
  primary: { label: string; to: string };
  secondary?: { label: string; to: string };
  titleId?: string;
  /** A branded close, with a separate approved-astronaut scene. */
  signature?: boolean;
}) {
  return (
    <Band space="standard" tone="surface" labelledBy={titleId} bare>
      <Container>
        <ScrollReveal className={signature ? 'p-cta p-cta--signature' : 'p-cta'}>
          {signature ? (
            <img
              className="p-cta__art"
              src={ART.ctaPair.src}
              width={ART.ctaPair.width}
              height={ART.ctaPair.height}
              alt=""
              loading="lazy"
              decoding="async"
            />
          ) : null}
          <h2 id={titleId} className="u-h2">{title}</h2>
          {body ? <p>{body}</p> : null}
          <div className="u-cluster u-cluster--center">
            <Btn to={primary.to}>{primary.label}</Btn>
            {secondary ? <Btn tone="secondary" to={secondary.to}>{secondary.label}</Btn> : null}
          </div>
        </ScrollReveal>
      </Container>
    </Band>
  );
}
